import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, Clock, TrendingUp, Users, ShieldCheck, MessageCircle, Network } from "lucide-react";
import PublicHeader from "../PublicHeader/PublicHeader";
import PublicContentWrap from "../PublicContentWrap/PublicContentWrap";
import Footer from "../Footer/Footer";
import Testimonials from "../Testimonials/Testimonials";
import SolutionsFAQ from "../SolutionsFAQ/SolutionsFAQ";
import ReadyToTransform from "../ReadyToTransform/ReadyToTransform";
import WhyTeamsAreSwitchingSection from "../WhyTeamsAreSwitchingSection/WhyTeamsAreSwitchingSection";

const DEFAULT_OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const DEFAULT_TRUSTED_BADGES = [
  "AI-driven workforce intelligence platform",
  "Ask TeamGrid: Get answers, not just data",
  "Understand flow, focus, and friction",
];

const DEFAULT_WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid (AI chat for reports and insights)",
  "Full workforce intelligence platform",
  "Real-time, actionable dashboards",
  "Daily, hourly, and weekly performance summaries",
  "Seamless onboarding and cross-team adoption",
  "Built-in team performance tracking",
  "Works across hybrid, remote and in-office environments",
  "Designed for clarity, not control",
];

const DEFAULT_COMPARISON_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Understand how and why work happens", competitor: "Track what employees are doing in real time" },
  { feature: "AI-Powered Insights", teamgrid: "Detects patterns, risks, and opportunities automatically", competitor: "Focuses on time logs and system usage" },
  { feature: "Workforce Intelligence", teamgrid: "AI-powered workforce intelligence platform", competitor: "Behavior-level monitoring and activity logging" },
  { feature: "Team Productivity Analytics", teamgrid: "Analyzes team-level trends, not just individuals", competitor: "Raw productivity data with little context" },
  { feature: "Team Performance Tracking", teamgrid: "Highlights blockers, delivery risks and team workload", competitor: "Measures inputs like hours or keystrokes" },
  { feature: "Privacy and Culture Fit", teamgrid: "Trusted by teams for transparency and improvement", competitor: "Often resisted due to oversight heavy features" },
  { feature: "Dashboards", teamgrid: "Real-time, summary-based with insight-led actions", competitor: "Real-time activity and system usage views" },
  { feature: "Export & Reporting", teamgrid: "One-click, presentation ready summaries", competitor: "CSV, raw data exports, basic reports" },
];

const ComparisonPageTemplate = ({
  heroBadge = null,
  heroTitleLine1 = "",
  heroTitleLine2 = null,
  heroTitleLine2Teal = false,
  heroTitleLine2TealPart = null,
  heroTitleLine3 = null,
  heroTitleLine3Teal = false,
  heroTitleLine4 = null,
  heroTitleLine4Teal = false,
  heroTitleFontSize = "clamp(28px, 4.5vw, 62px)",
  heroDescription = "",
  heroDescriptionLines = null,
  heroDescriptionLinesNoWrap = false,
  heroDescriptionFontSize = 14,
  heroDescriptionLineHeight = "22px",
  competitorName = "",
  competitorCardLabel = null,
  competitorLogoUrl = null,
  trustedBadges = DEFAULT_TRUSTED_BADGES,
  faqs = [],
  faqTitle = "Frequently Asked Questions",
  faqSubtitle = "Common questions about TeamGrid and how we compare.",
  comparisonTableRows = null,
  dataDecisionsHeading = null,
  dataDecisionsSubtitle = null,
  dataDecisionsTeamGridBullets = null,
  dataDecisionsCompetitorBullets = null,
  dataDecisionsCompetitorCardTitle = null,
  dataDecisions2Heading = null,
  dataDecisions2Subtitle = null,
  dataDecisions2TeamGridBullets = null,
  dataDecisions2CompetitorBullets = null,
  dataDecisions2CompetitorCardTitle = null,
  dataDecisions2ConstrainHeight = false,
  keyFeaturesTitle = "Why Teams Choose TeamGrid Over Monitoring Tools",
  keyFeaturesSubtitle = "Ask TeamGrid – Your AI Workforce Assistant",
  keyFeatureCards = null,
  keyFeaturesSectionWhyTeamsSwitching = false,
  whatYouGetItems = null,
  whatYouGetSectionPadding80_384 = false,
  dataDecisionsSwapLayout = false,
  dataDecisionsSectionsReversed = false,
  dataDecisionsImageSrc = "/Background+Border_bg_middle.webp",
  dataDecisions2ImageSrc = "/left_border_1.webp",
  outcomesImageSrc = "/Section_new1.webp",
  outcomesImageAlt = "Performance Outcomes Teams See - Faster decisions with less data crunching, Proactive improvements vs. Reactive management, Higher trust and adoption, No need for employee surveillance to get results",
  outcomesItems = null,
  outcomesTitle = "Performance Outcomes Teams See",
  children = null,
  // SEO (optional) - when set, renders Helmet + JSON-LD
  seoTitle = null,
  seoDescription = null,
  seoCanonical = null,
  seoOgImage = null,
  jsonLdFaq = null,
  jsonLdGraph: jsonLdGraphOverride = null, // when set, used instead of default WebSite/Organization/WebPage graph
}) => {
  const gridItems = whatYouGetItems != null ? whatYouGetItems : DEFAULT_WHAT_YOU_GET_ITEMS;
  const tableRows = comparisonTableRows != null ? comparisonTableRows : DEFAULT_COMPARISON_TABLE_ROWS;
  const sectionHeading = dataDecisionsHeading ?? "Moving from Data to Decisions";
  const sectionSubtitle = dataDecisionsSubtitle ?? "Why Insight Beats a Basic Productivity Tracking Tool";
  const competitorCardTitle = dataDecisionsCompetitorCardTitle != null
    ? dataDecisionsCompetitorCardTitle
    : `With ${competitorCardLabel || competitorName || "Employee Monitoring Software"}`;
  const teamgridBullets = dataDecisionsTeamGridBullets ?? [
    "Team productivity analytics highlight inefficiencies and friction",
    "Ask TeamGrid for real-time insight like: \"Where are we losing time?\" or \"Who's overloaded right now?\"",
    "Clear, automated summaries enable fast, informed decisions",
  ];
  const competitorBullets = dataDecisionsCompetitorBullets ?? [
    "Tracks time, activity, and sometimes screen use",
    "Focuses on employee productivity metrics without interpretation",
    "Creates overhead for managers who must make sense of the data",
  ];
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hasSEO = seoTitle && seoDescription && seoCanonical;
  const ogImage = seoOgImage != null ? seoOgImage : DEFAULT_OG_IMAGE;
  const defaultGraph = hasSEO
    ? {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "WebSite", "@id": "https://teamgrid.ai/#website", url: "https://teamgrid.ai/", name: "TeamGrid" },
          { "@type": "Organization", "@id": "https://teamgrid.ai/#organization", name: "TeamGrid", url: "https://teamgrid.ai/" },
          {
            "@type": "WebPage",
            "@id": `${seoCanonical}#webpage`,
            url: seoCanonical,
            name: seoTitle,
            isPartOf: { "@id": "https://teamgrid.ai/#website" },
            about: { "@id": "https://teamgrid.ai/#organization" },
          },
        ],
      }
    : null;
  const jsonLdGraphToRender = hasSEO && jsonLdGraphOverride != null ? jsonLdGraphOverride : defaultGraph;

  return (
    <div className="min-h-screen bg-white text-[#232323] overflow-x-hidden">
      {hasSEO && (
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={seoCanonical} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoCanonical} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:site_name" content="TeamGrid" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={seoCanonical} />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={ogImage} />
          {jsonLdGraphToRender != null && (
            <script type="application/ld+json">
              {JSON.stringify(jsonLdGraphToRender)}
            </script>
          )}
          {jsonLdFaq != null && (
            <script type="application/ld+json">
              {JSON.stringify(jsonLdFaq)}
            </script>
          )}
        </Helmet>
      )}
      <PublicHeader />
      <main className="relative">
        <PublicContentWrap className="flex flex-col">
        {/* Hero Section - aligned with header (logo to trial CTA) */}
        <section
          className="relative flex flex-col items-center w-full pt-[120px] pb-[120px] overflow-hidden"
          style={{
            background:
              "radial-gradient(70.09% 142.7% at 50% 0%, rgba(0, 196, 180, 0.06) 0%, rgba(0, 196, 180, 0) 60%), linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)",
          }}
        >
          <div className="w-full min-w-0 max-w-[896px] mx-auto flex flex-col items-center text-center">
            {heroBadge && (
              <div
                className="inline-flex items-center justify-center px-4 py-2 rounded-full mb-6"
                style={{
                  background: "transparent",
                  border: "1px solid #00C4B4",
                }}
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-[#00C4B4]">
                  {heroBadge}
                </span>
              </div>
            )}
            {/* Hero H1: do not change position or size (className, heroTitleFontSize, lineHeight, letterSpacing, or parent max-w) */}
            <h1
              className="font-extrabold text-center text-[#111827] mb-4 flex flex-col items-center gap-0 leading-tight max-w-full break-words"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: heroTitleFontSize,
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
              }}
            >
              {heroTitleLine2 != null && heroTitleLine2 !== "" ? (
                <>
                  <span className="block leading-tight text-balance">{heroTitleLine1}</span>
                  {heroTitleLine2TealPart != null ? (
                    <span className="block leading-tight">
                      <span>{heroTitleLine2}</span>
                      <span className="text-[#00C4B4]">{heroTitleLine2TealPart}</span>
                    </span>
                  ) : (
                    <span className={`block leading-tight ${heroTitleLine2Teal ? "text-[#00C4B4]" : ""}`}>
                      {heroTitleLine2}
                    </span>
                  )}
                  {heroTitleLine3 != null && heroTitleLine3 !== "" && (
                    <span className={`block leading-tight ${heroTitleLine3Teal ? "text-[#00C4B4]" : ""}`}>
                      {heroTitleLine3}
                    </span>
                  )}
                  {heroTitleLine4 != null && heroTitleLine4 !== "" && (
                    <span className={`block leading-tight ${heroTitleLine4Teal ? "text-[#00C4B4]" : ""}`}>
                      {heroTitleLine4}
                    </span>
                  )}
                </>
              ) : (
                <>
                  {heroTitleLine1}
                  {heroTitleLine3 != null && heroTitleLine3 !== "" && (
                    <>
                      <br />
                      <span className={heroTitleLine3Teal ? "text-[#00C4B4]" : ""}>
                        {heroTitleLine3}
                      </span>
                    </>
                  )}
                </>
              )}
            </h1>
            {heroDescriptionLines && heroDescriptionLines.length > 0 ? (
              <p
                className="text-center text-[#64748B] max-w-[600px] mx-auto mb-10 min-w-0 break-words"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: heroDescriptionFontSize,
                  lineHeight: heroDescriptionLineHeight,
                }}
              >
                {heroDescriptionLines.map((line, i) => (
                  <React.Fragment key={i}>
                    {heroDescriptionLinesNoWrap ? (
                      <span className="block sm:whitespace-nowrap">{line}</span>
                    ) : (
                      <>
                        {line}
                        {i < heroDescriptionLines.length - 1 && <br />}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </p>
            ) : (
              <p
                className="text-center text-[#64748B] max-w-[600px] mx-auto mb-10 min-w-0 break-words"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: heroDescriptionFontSize,
                  lineHeight: heroDescriptionLineHeight,
                }}
              >
                {heroDescription}
              </p>
            )}

            {/* VS Visual - TeamGrid logo | VS | competitor logo; 96px rounded squares */}
            <div className="w-full overflow-x-auto overflow-y-hidden mb-10 flex justify-center">
              <div className="inline-flex flex-row flex-nowrap items-center gap-6 shrink-0">
                <div
                  className="w-[96px] h-[96px] rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center bg-transparent"
                >
                  <img
                    src="/Container_1st.webp"
                    alt="TeamGrid Logo"
                    width={96}
                    height={96}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span
                  className="text-[#64748B] font-semibold text-base uppercase tracking-tight flex-shrink-0"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  VS
                </span>
                <div
                  className="w-[96px] h-[96px] rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center bg-transparent"
                >
                  <img
                    src={competitorLogoUrl || "/Background+Border_2nd.webp"}
                    alt={competitorName}
                    width={96}
                    height={96}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg font-bold text-base text-white transition-all hover:opacity-90"
                style={{
                  background: "#00C4B4",
                  borderRadius: "8px",
                  boxShadow: "0px 0px 20px rgba(0, 196, 180, 0.25)",
                }}
              >
                Get Started Free
              </button>
              <button
                type="button"
                onClick={() => navigate("/book-demo")}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-bold text-sm text-[#111827] bg-white border border-[#E5E7EB] transition-all hover:bg-gray-50"
                style={{ borderRadius: "8px" }}
              >
                <span aria-hidden>🗓</span>
                Book a 15-Min Demo
              </button>
            </div>

            {/* Trusted badges - single row with pipe separators */}
            <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-2 text-[12px] sm:text-[13px] text-[#9CA3AF]" style={{ fontFamily: "Inter, sans-serif" }}>
              {trustedBadges.map((text, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="flex-shrink-0 px-1">|</span>}
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 flex-shrink-0" />
                    {text}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Section 1 - TeamGrid vs Monitoring Software (Feature by Feature + table) */}
        <section className="w-full bg-white flex flex-col items-center py-16 md:py-20 gap-12">
          <div className="flex flex-col items-center gap-4 max-w-[1240px] w-full">
            <div className="inline-flex items-center justify-center px-5 py-2 rounded-full bg-[rgba(0,209,193,0.06)] border border-[rgba(0,209,193,0.15)]">
              <span className="text-[13px] font-semibold uppercase tracking-wider text-[#00D1C1]" style={{ fontFamily: "Inter, sans-serif" }}>Feature by Feature</span>
            </div>
            <h2 className="font-bold text-center text-black text-3xl sm:text-4xl md:text-[48px] leading-tight tracking-[-1.44px] max-w-[745px] whitespace-nowrap" style={{ fontFamily: "Inter, sans-serif" }}>
              TeamGrid vs {competitorName || "Monitoring Software"}
            </h2>
            <p className="text-center text-[20px] leading-[31px] text-[#64748B] max-w-[600px] whitespace-nowrap" style={{ fontFamily: "Inter, sans-serif" }}>
              See exactly how the two platforms compare across every dimension.
            </p>
          </div>
          <div className="w-full max-w-[1240px] rounded-3xl border border-black/10 overflow-hidden bg-white">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-black/10">
                  <th className="text-left py-6 px-8 text-sm font-semibold uppercase tracking-wide text-[#475569] w-[40%]" style={{ fontFamily: "Inter, sans-serif" }}>Feature</th>
                  <th className="text-center py-6 px-8 text-sm font-semibold uppercase tracking-wide text-[#475569]" style={{ fontFamily: "Inter, sans-serif" }}>
                    <span className="inline-flex items-center justify-center gap-2">
                      <img src="/logo_besides_1.webp" alt="TeamGrid logo" className="w-8 h-8 object-contain flex-shrink-0" />
                      TeamGrid
                    </span>
                  </th>
                  <th className="text-center py-6 px-8 text-sm font-semibold uppercase tracking-wide text-[#475569]" style={{ fontFamily: "Inter, sans-serif" }}>
                    <span className="inline-flex items-center justify-center gap-2">
                      <img src={competitorLogoUrl || "/logo_besides_2.webp"} alt={competitorName ? `${competitorName} logo` : "Competitor"} className="w-8 h-8 object-contain flex-shrink-0" />
                      {competitorName}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr key={i} className="border-b border-black/10">
                    <td className="py-5 px-8 text-[15px] font-semibold text-black" style={{ fontFamily: "Inter, sans-serif" }}>{row.feature}</td>
                    <td className="py-5 px-8 text-[13px] leading-[22px] text-[#6B6B6B] text-center" style={{ fontFamily: "Inter, sans-serif" }}>{row.teamgrid}</td>
                    <td className="py-5 px-8 text-[13px] leading-[22px] text-[#6B6B6B] text-center" style={{ fontFamily: "Inter, sans-serif" }}>{row.competitor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 2 & 2b - Data to Decisions (order reversed when dataDecisionsSectionsReversed) */}
        <div className="flex flex-col">
        {/* Section 2 - Moving from Data to Decisions */}
        <section className={`w-full bg-white flex flex-col items-start py-16 md:py-20 ${dataDecisionsSectionsReversed && dataDecisions2Heading != null ? "order-2" : "order-1"}`}>
          <div className="w-full max-w-[1152px] mx-auto flex flex-col gap-8 lg:gap-10">
            {/* Row: heading + two cards (left) | illustration panel (right) — order swapped when dataDecisionsSwapLayout */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start lg:items-stretch">
              {/* Left: heading on top of the two cards; align bottom with right column on lg */}
              <div className={`flex flex-col items-start gap-5 pb-8 lg:pb-0 w-full lg:max-w-[588px] lg:justify-end ${dataDecisionsSwapLayout ? "order-1 lg:order-2" : "order-1 lg:order-1"}`}>
                <div className="flex flex-col gap-5">
                  <h2
                    className="font-bold text-black text-[28px] sm:text-[32px] md:text-[36px] leading-[43px] tracking-[-0.72px]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {sectionHeading}
                  </h2>
                  <p
                    className="text-[17px] leading-[29px] text-[#475569]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {sectionSubtitle}
                  </p>
                </div>
                <div className="flex flex-col gap-5 w-full">
                <div className="bg-[#F8FAFC] rounded-2xl p-7 flex flex-col gap-4 text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(34,197,94,0.1)]">
                      <img src="/logo-icon.svg" alt="TeamGrid icon" className="w-5 h-5 object-contain" />
                    </div>
                    <span className="font-bold text-base text-black leading-[27px]" style={{ fontFamily: "Inter, sans-serif" }}>TeamGrid</span>
                  </div>
                  <ul className="flex flex-col gap-0 list-none pl-0 m-0">
                    {teamgridBullets.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 py-2">
                        <span className="font-bold text-sm text-[#22C55E] flex-shrink-0 w-4 text-center leading-[22px]" style={{ fontFamily: "Inter, sans-serif" }}>→</span>
                        <span className="text-sm leading-[22px] text-[#475569] flex-1 min-w-0" style={{ fontFamily: "Inter, sans-serif" }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#F8FAFC] rounded-2xl p-7 flex flex-col gap-4 text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#F1F5F9]">
                      <img src={competitorLogoUrl || "/logo_besides_2.webp"} alt={competitorName ? `${competitorName} logo` : "Competitor"} className="w-8 h-8 object-contain" />
                    </div>
                    <span className="font-bold text-base text-black leading-[27px]" style={{ fontFamily: "Inter, sans-serif" }}>{competitorCardTitle}</span>
                  </div>
                  <ul className="flex flex-col gap-0 list-none pl-0 m-0">
                    {competitorBullets.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 py-2">
                        <span className="font-bold text-sm text-[#EF4444] flex-shrink-0 w-4 text-center leading-[22px]" style={{ fontFamily: "Inter, sans-serif" }}>→</span>
                        <span className="text-sm leading-[22px] text-[#475569] flex-1 min-w-0" style={{ fontFamily: "Inter, sans-serif" }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              </div>
              {/* Right lane: illustration at bottom; 670px for full-bleed image, 500px + contained for left_border_1 */}
              <div className={`w-full lg:max-w-[500px] shrink-0 flex flex-col lg:justify-end ${dataDecisionsSwapLayout ? "order-2 lg:order-1" : "order-2 lg:order-2"}`}>
                <div className={`w-full lg:max-w-[500px] rounded-3xl flex items-center justify-center ${dataDecisionsImageSrc === "/left_border_1.webp" ? "min-h-[400px] lg:h-[500px] lg:max-h-[500px] bg-[#F1F5F9] border border-white/10 p-12 lg:p-16" : "min-h-[400px] lg:h-[670px] lg:max-h-[670px] overflow-hidden"}`}>
                  <img
                    src={dataDecisionsImageSrc}
                    alt={sectionHeading || "Data to decisions illustration"}
                    className={dataDecisionsImageSrc === "/left_border_1.webp" ? "w-full max-w-[240px] h-auto object-contain opacity-90" : "w-full h-full object-cover"}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2b - Second Data to Decisions (optional, e.g. We360 "From Monitoring Data to Meaningful Decisions") */}
        {dataDecisions2Heading != null && (
        <section
          className={`w-full bg-white flex flex-col items-start ${dataDecisions2ConstrainHeight ? "py-[80px]" : "py-16 md:py-20"} ${dataDecisionsSectionsReversed ? "order-1" : "order-2"}`}
        >
          <div className="w-full max-w-[1152px] mx-auto flex flex-col gap-8 lg:gap-16">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start lg:items-stretch">
              {/* Left: illustration */}
              <div className={`w-full lg:max-w-[500px] shrink-0 flex flex-col lg:justify-end ${dataDecisionsSwapLayout ? "order-2 lg:order-2" : "order-2 lg:order-1"}`}>
                <div className={`w-full lg:max-w-[500px] rounded-3xl flex items-center justify-center ${dataDecisions2ImageSrc === "/left_border_1.webp" ? "min-h-[400px] lg:h-[500px] lg:max-h-[500px] bg-[#F1F5F9] border border-white/10 p-12 lg:p-16" : "min-h-[400px] lg:h-[670px] lg:max-h-[670px] overflow-hidden"}`}>
                  <img
                    src={dataDecisions2ImageSrc}
                    alt={dataDecisions2Heading || "Data to decisions illustration"}
                    className={dataDecisions2ImageSrc === "/left_border_1.webp" ? "w-full max-w-[240px] h-auto object-contain opacity-90" : "w-full h-full object-cover"}
                  />
                </div>
              </div>
              {/* Right: heading + two cards; align bottom with left illustration on lg */}
              <div className={`flex flex-col items-start gap-5 pb-8 lg:pb-0 w-full lg:max-w-[588px] lg:justify-end ${dataDecisionsSwapLayout ? "order-1 lg:order-1" : "order-1 lg:order-2"}`}>
                <div className="flex flex-col gap-5">
                  <h2
                    className="font-bold text-black text-[28px] sm:text-[32px] md:text-[36px] leading-[43px] tracking-[-0.72px]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {dataDecisions2Heading}
                  </h2>
                  <p
                    className="text-[17px] leading-[29px] text-[#475569]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {dataDecisions2Subtitle}
                  </p>
                </div>
                <div className="flex flex-col gap-5 w-full">
                  <div className="bg-[#F8FAFC] rounded-2xl p-7 flex flex-col gap-4 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(34,197,94,0.1)]">
                        <img src="/logo-icon.svg" alt="TeamGrid icon" className="w-5 h-5 object-contain" />
                      </div>
                      <span className="font-bold text-base text-black leading-[27px]" style={{ fontFamily: "Inter, sans-serif" }}>TeamGrid</span>
                    </div>
                    <ul className="flex flex-col gap-0 list-none pl-0 m-0">
                      {(dataDecisions2TeamGridBullets || []).map((item, i) => (
                        <li key={i} className="flex items-start gap-3 py-2">
                          <span className="font-bold text-sm text-[#22C55E] flex-shrink-0 w-4 text-center leading-[22px]" style={{ fontFamily: "Inter, sans-serif" }}>→</span>
                          <span className="text-sm leading-[22px] text-[#475569] flex-1 min-w-0" style={{ fontFamily: "Inter, sans-serif" }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#F8FAFC] rounded-2xl p-7 flex flex-col gap-4 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#F1F5F9]">
                        <img src={competitorLogoUrl || "/logo_besides_2.webp"} alt={competitorName ? `${competitorName} logo` : "Competitor"} className="w-8 h-8 object-contain" />
                      </div>
                      <span className="font-bold text-base text-black leading-[27px]" style={{ fontFamily: "Inter, sans-serif" }}>{dataDecisions2CompetitorCardTitle ?? competitorCardLabel ?? competitorName ?? "We360"}</span>
                    </div>
                    <ul className="flex flex-col gap-0 list-none pl-0 m-0">
                      {(dataDecisions2CompetitorBullets || []).map((item, i) => (
                        <li key={i} className="flex items-start gap-3 py-2">
                          <span className="font-bold text-sm text-[#EF4444] flex-shrink-0 w-4 text-center leading-[22px]" style={{ fontFamily: "Inter, sans-serif" }}>→</span>
                          <span className="text-sm leading-[22px] text-[#475569] flex-1 min-w-0" style={{ fontFamily: "Inter, sans-serif" }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}
        </div>

        {/* Section 3 - Why Teams Choose TeamGrid / Why Teams Are Switching */}
        {keyFeaturesSectionWhyTeamsSwitching ? (
          <section
            className="w-full flex flex-col bg-[#F4F6F8] border-t border-[#E5E7EB] items-center py-[80px]"
            style={{ boxSizing: "border-box" }}
          >
            <WhyTeamsAreSwitchingSection asInnerContent />
          </section>
        ) : (
        <section className="w-full bg-[#F8FAFC] flex flex-col items-center py-16 md:py-20">
          <div className="w-full max-w-[1320px] mx-auto flex flex-col gap-16 items-center">
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="inline-flex items-center justify-center rounded-[20px] bg-[rgba(0,209,193,0.06)] border border-[rgba(0,209,193,0.15)] px-6 py-2.5">
                <span className="text-[13px] font-semibold uppercase tracking-[0.65px] text-[#00D1C1]" style={{ fontFamily: "Inter, sans-serif" }}>KEY FEATURES</span>
              </div>
              <h2 className="font-bold text-black text-center tracking-[-1.44px] whitespace-nowrap" style={{ fontFamily: "Inter, sans-serif", fontSize: "clamp(1.5rem, 4vw, 48px)", lineHeight: "1.7" }}>
                {keyFeaturesTitle}
              </h2>
              <p className="text-[20px] leading-[31px] text-[#64748B] max-w-[600px] text-center" style={{ fontFamily: "Inter, sans-serif" }}>
                {keyFeaturesSubtitle}
              </p>
            </div>
            <div className="flex flex-row flex-wrap justify-center gap-6 w-full max-w-[1320px] px-0 md:px-8">
              {keyFeatureCards != null && keyFeatureCards.length >= 3 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-[1240px]">
                  {[
                    { Icon: MessageCircle, bgClass: "bg-[rgba(242,103,58,0.12)]", iconClass: "text-[#F2673A]" },
                    { Icon: Users, bgClass: "bg-[rgba(100,116,139,0.12)]", iconClass: "text-[#475569]", imageSrc: "/3rd_card_image.webp" },
                    { Icon: Network, bgClass: "bg-[rgba(0,209,193,0.12)]", iconClass: "text-[#0D9488]" },
                  ].map(({ Icon, bgClass, iconClass, imageSrc }, i) => (
                    <div key={i} className="bg-white border border-[#E5E7EB] rounded-[12px] p-6 flex flex-col text-left shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                      <div className={`w-12 h-12 rounded-[10px] flex items-center justify-center flex-shrink-0 mb-4 overflow-hidden ${bgClass}`}>
                        {imageSrc ? (
                          <img src={imageSrc} alt={keyFeatureCards[i]?.title || "Feature"} className="w-full h-full object-contain" />
                        ) : (
                          <Icon className={`w-6 h-6 ${iconClass}`} strokeWidth={2} />
                        )}
                      </div>
                      <h3 className="font-bold text-[18px] leading-[1.35] text-[#1E293B] mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
                        {keyFeatureCards[i].title}
                      </h3>
                      <p className="text-[15px] leading-[1.6] text-[#64748B]" style={{ fontFamily: "Inter, sans-serif" }}>
                        {keyFeatureCards[i].intro}
                      </p>
                    </div>
                  ))}
                </div>
              ) : keyFeatureCards != null && keyFeatureCards.length >= 2 ? (
                <>
                  <div className="w-full md:max-w-[420px] md:w-[420px] min-h-[374px] bg-white border border-white/10 rounded-[20px] p-8 flex flex-col flex-1 text-left">
                    <div className="w-14 h-14 flex-shrink-0 rounded-[10.67px] overflow-hidden flex items-center justify-center mb-5">
                      <img src="/logo_1_workforce_intelligence.webp" alt="Workforce intelligence" className="w-full h-full object-contain" />
                    </div>
                    <h3 className="font-bold text-[20px] leading-[26px] text-[#475569] mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                      {keyFeatureCards[0].title}
                    </h3>
                    <div className="flex flex-col gap-3 text-left flex-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        {keyFeatureCards[0].intro}
                      </p>
                      <ul className="list-disc list-inside text-[15px] leading-[24px] text-[#475569] space-y-1 pl-1">
                        {(keyFeatureCards[0].bullets || []).map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        {keyFeatureCards[0].closing}
                      </p>
                    </div>
                  </div>
                  <div className="w-full md:max-w-[420px] md:w-[420px] min-h-[374px] bg-white border border-white/10 rounded-[20px] p-8 flex flex-col flex-1 text-left">
                    <div className="w-14 h-14 flex-shrink-0 rounded-[10.67px] overflow-hidden flex items-center justify-center mb-5">
                      <img src="/logo_2_workforce_intelligence.webp" alt="Organizational intelligence" className="w-full h-full object-contain" />
                    </div>
                    <h3 className="font-bold text-[20px] leading-[26px] text-[#475569] mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                      {keyFeatureCards[1].title}
                    </h3>
                    <div className="flex flex-col gap-3 text-left flex-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        {keyFeatureCards[1].intro}
                      </p>
                      <ul className="list-disc list-inside text-[15px] leading-[24px] text-[#475569] space-y-1 pl-1">
                        {(keyFeatureCards[1].bullets || []).map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        {keyFeatureCards[1].closing}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full md:max-w-[420px] md:w-[420px] min-h-[374px] bg-white border border-white/10 rounded-[20px] p-8 flex flex-col flex-1 text-left">
                    <div className="w-14 h-14 flex-shrink-0 rounded-[10.67px] overflow-hidden flex items-center justify-center mb-5">
                      <img src="/logo_1_workforce_intelligence.webp" alt="Workforce intelligence" className="w-full h-full object-contain" />
                    </div>
                    <h3 className="font-bold text-[20px] leading-[26px] text-[#475569] mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                      Ask TeamGrid – Your AI Workforce Assistant
                    </h3>
                    <div className="flex flex-col gap-3 text-left flex-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        With TeamGrid, you can ask practical, forward looking questions such as:
                      </p>
                      <ul className="list-disc list-inside text-[15px] leading-[24px] text-[#475569] space-y-1 pl-1">
                        <li>What&apos;s slowing us down right now?</li>
                        <li>Which functions are over capacity?</li>
                        <li>Are there signs of burnout or delivery risk?</li>
                      </ul>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        TeamGrid responds instantly with clear, downloadable summaries no dashboard digging required.
                      </p>
                    </div>
                  </div>
                  <div className="w-full md:max-w-[420px] md:w-[420px] min-h-[374px] bg-white border border-white/10 rounded-[20px] p-8 flex flex-col flex-1 text-left">
                    <div className="w-14 h-14 flex-shrink-0 rounded-[10.67px] overflow-hidden flex items-center justify-center mb-5">
                      <img src="/logo_2_workforce_intelligence.webp" alt="Organizational intelligence" className="w-full h-full object-contain" />
                    </div>
                    <h3 className="font-bold text-[20px] leading-[26px] text-[#475569] mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                      Organizational Intelligence, Not Just Monitoring
                    </h3>
                    <div className="flex flex-col gap-3 text-left flex-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        TeamGrid helps you uncover:
                      </p>
                      <ul className="list-disc list-inside text-[15px] leading-[24px] text-[#475569] space-y-1 pl-1">
                        <li>Workflow inefficiencies</li>
                        <li>Signs of overload or underutilization</li>
                        <li>Cross-team friction points</li>
                        <li>Performance risks before they escalate</li>
                      </ul>
                      <p className="text-[15px] leading-[24px] text-[#475569] font-normal">
                        All surfaced through AI not through direct monitoring or behavior control.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
        )}

        {/* Custom content (feature comparison, etc.) */}
        {children}

        {/* Testimonials */}
        <Testimonials
          title="Trusted by Leading Teams"
          subtitle="See what our customers say about TeamGrid"
        />

        {/* Section - What You'll Get Grid (after Testimonials) */}
        <section
          className={`w-full bg-[#F8FAFC] flex flex-col items-center ${whatYouGetSectionPadding80_384 ? "py-[80px] min-h-[705px]" : "py-16 md:py-20"}`}
          style={whatYouGetSectionPadding80_384 ? { boxSizing: "border-box" } : undefined}
        >
          <div className="w-full max-w-[1320px] mx-auto flex flex-col items-center gap-16 px-0 md:px-10">
            <div className="flex flex-col items-center gap-5 text-center w-full max-w-[1240px]">
              <div className="inline-flex items-center justify-center rounded-[20px] bg-[rgba(0,209,193,0.06)] border border-[rgba(0,209,193,0.15)] px-6 py-2.5">
                <span className="text-[13px] font-semibold uppercase tracking-[0.65px] text-[#00D1C1]" style={{ fontFamily: "Inter, sans-serif" }}>Complete Package</span>
              </div>
              <h2 className="font-bold text-black text-center tracking-[-1.44px]" style={{ fontFamily: "Inter, sans-serif", fontSize: "clamp(1.5rem, 4vw, 48px)", lineHeight: "1.7" }}>
                What You&apos;ll Get with TeamGrid
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-[1240px]">
              {gridItems.map((label, i) => (
                <div
                  key={i}
                  className="bg-white border border-black/10 rounded-[14px] p-5 flex flex-row items-start gap-3.5"
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(34,197,94,0.1)]">
                    <span className="font-bold text-sm leading-6 text-[#22C55E]" style={{ fontFamily: "Plus Jakarta Sans, Inter, sans-serif" }}>✓</span>
                  </div>
                  <p className="font-medium text-[15px] leading-6 text-[#475569] pt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section - Outcomes You'll See (image or custom banner) */}
        <section className="w-full flex flex-col items-center py-16 md:py-20">
          <div className="w-full max-w-[1240px] rounded-[28px] overflow-hidden">
            {outcomesItems != null && outcomesItems.length >= 4 ? (
              <div
                className="w-full rounded-[28px] px-8 py-10 sm:px-10 sm:py-12 md:px-14 md:py-14 flex flex-col items-center gap-8 md:gap-10"
                style={{
                  background: "linear-gradient(90deg, #6B21A8 0%, #A21CAF 35%, #BE185D 70%, #DC2626 100%)",
                  boxSizing: "border-box",
                }}
              >
                <h2
                  className="text-white font-bold text-center text-xl sm:text-2xl md:text-3xl"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {outcomesTitle}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 w-full">
                  {[Clock, TrendingUp, Users, ShieldCheck].slice(0, outcomesItems.length).map((Icon, i) => (
                    <div key={i} className="flex flex-col items-center justify-center gap-3 text-center">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <Icon className="w-10 h-10 text-white" strokeWidth={1.5} stroke="currentColor" fill="none" />
                      </div>
                      <span className="text-white text-sm sm:text-base font-medium" style={{ fontFamily: "Inter, sans-serif" }}>
                        {outcomesItems[i]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <img src={outcomesImageSrc} alt={outcomesImageAlt} className="w-full h-auto object-cover" />
            )}
          </div>
        </section>

        {/* FAQ */}
        {faqs && faqs.length > 0 && (
          <section
            className="flex flex-col justify-center items-center pt-20 pb-16 text-[#232323]"
            style={{ background: "rgba(0, 0, 0, 0.05)" }}
          >
            <div className="w-full max-w-[768px] flex flex-col items-start px-6 gap-12">
              <SolutionsFAQ
                faqs={faqs}
                title={faqTitle}
                subtitle={faqSubtitle}
              />
            </div>
          </section>
        )}

        </PublicContentWrap>

        {/* Ready CTA - full-width black bar */}
        <div className="mt-0">
          <ReadyToTransform />
        </div>

        {/* Main Footer */}
        <div className="w-full max-w-[1919px] mx-auto">
          <Footer />
        </div>
      </main>
    </div>
  );
};

export default ComparisonPageTemplate;
